import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  RefreshCw, 
  Trash2,
  ClipboardList,
  Receipt,
  Plus,
  Minus,
  AlertTriangle,
  CalendarIcon,
  CheckCircle,
  XCircle,
  Clock,
  Info,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { StatusBadge } from '@/components/common/StatusBadge';

interface SubscriptionItemDetail {
  id: string;
  subscription_id: string;
  subscription_plan_id: string;
  quantity: number;
  unit_price: number;
  state: string | null;
  current_term_start: string | null;
  current_term_end: string | null;
  currency_code: string | null;
  next_billing_at: string | null;
  amount_price: number | null;
  canceled_at: string | null;
  cancellation_reason: string | null;
  billing_start_terms: string | null;
  billing_cycles: number | null;
  term_start: string | null;
  term_end: string | null;
  billing_by: string | null;
  auto_renew: boolean | null;
  created_at: string;
  updated_at?: string;
  subscription: {
    id: string;
    state: string;
    customer_relationship: {
      id: string;
      relationship?: string;
      customer: {
        id: string;
        name: string;
        legal_name: string;
      }
    }
  };
  subscription_plan: {
    id: string;
    name: string;
    description: string | null;
    billing_frequency: string;
    sku: string | null;
    family_id: string | null;
    family: {
      id: string;
      name: string;
    } | null;
    prices: {
      id: string;
      currency: string;
      price_amount: number;
      cost_amount: number | null;
    }[];
    billing_period_type: string | null;
    billing_period_duration: string | null;
  };
  orders: {
    id: string;
    subscription_id?: string;
    operation_type: string;
    quantity_change: number | null;
    operation_date: string;
    created_at: string;
    notes?: string;
    subscription_item?: {
      subscription_plan?: {
        name: string;
      }
    };
  }[];
  invoice_items: {
    id: string;
    invoice_number: string;
    invoice_date: string;
    amount_due: number;
    currency: string;
    due_date: string | null;
    paid_date: string | null;
    status: string;
  }[];
}

const SubscriptionItemDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionItem, setSubscriptionItem] = useState<SubscriptionItemDetail | null>(null);
  const [operations, setOperations] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('details');
  const [isQuantityChangeDialogOpen, setIsQuantityChangeDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [quantityChangeType, setQuantityChangeType] = useState<'aumento' | 'redução'>('aumento');
  const [originalQuantity, setOriginalQuantity] = useState(0);
  const [newQuantity, setNewQuantity] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(new Date());
  const [invoiceDueDate, setInvoiceDueDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() + 30))
  );
  const { toast } = useToast();

  useEffect(() => {
    if (id) {
      fetchSubscriptionItemDetails(id);
    } else {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'ID do item da assinatura não fornecido'
      });
    }
  }, [id]);

  const fetchSubscriptionItemDetails = async (itemId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_items')
        .select(`
          *,
          subscription:subscription_id (
            id,
            state,
            customer_relationship:customer_relationship_id (
              id,
              relationship,
              customer:customer_id (
                id,
                name,
                legal_name
              )
            )
          ),
          subscription_plan:subscription_plan_id (
            id,
            name,
            description,
            billing_frequency,
            sku,
            family_id,
            family:family_id (
              id,
              name
            ),
            prices:subscription_plan_prices (
              id,
              currency,
              price_amount,
              cost_amount
            ),
            billing_period_type,
            billing_period_duration
          )
        `)
        .eq('id', itemId)
        .single();

      if (error) throw error;
      
      console.log("Fetched subscription item data:", data);
      
      // Ensure we have all required fields for TypeScript
      if (!data.created_at) {
        data.created_at = new Date().toISOString();
      }
      
      // Use type casting to avoid TypeScript errors
      const typedData = data as SubscriptionItemDetail;
      
      // Initialize with empty arrays for orders and invoice_items
      const subscriptionItemWithArrays: SubscriptionItemDetail = {
        ...typedData,
        orders: [],
        invoice_items: []
      };
      
      setSubscriptionItem(subscriptionItemWithArrays);
      setOriginalQuantity(data.quantity);
      setNewQuantity(data.quantity);
      setCancellationReason('');
      
      // Buscar histórico de operações
      await refreshOrders();
      
      // Buscar faturas
      await refreshInvoices();
    } catch (error) {
      console.error('Erro ao buscar detalhes do item:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível carregar os detalhes do item'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async () => {
    if (!subscriptionItem || !id) return;
    
    setLoading(true);
    try {
      console.log('Buscando ordens para a assinatura:', subscriptionItem.subscription_id);
      
      // Get all orders for this subscription
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          subscription_id,
          subscription_plan_id,
          operation_type,
          quantity_change,
          operation_date,
          created_at,
          notes
        `)
        .eq('subscription_id', subscriptionItem.subscription_id)
        .order('operation_date', { ascending: false });

      if (ordersError) {
        console.error('Erro ao buscar ordens:', ordersError);
        throw ordersError;
      }

      console.log('Ordens encontradas:', ordersData);
      
      // Filter orders to only include those relevant to this subscription item
      // (orders with matching subscription_plan_id or without a specific plan)
      const relevantOrders = (ordersData || []).filter(order => {
        return order.subscription_plan_id === subscriptionItem.subscription_plan_id || 
               !order.subscription_plan_id; // Include general subscription operations
      });
      
      // Convert to the expected format
      const formattedOrders = relevantOrders.map(order => ({
        id: order.id,
        subscription_id: order.subscription_id,
        operation_type: order.operation_type,
        quantity_change: order.quantity_change,
        operation_date: order.operation_date,
        created_at: order.created_at,
        notes: order.notes || '',
        subscription_item: {
          subscription_plan: {
            name: "Current Plan" // Since we don't have plan details in the orders table
          }
        }
      }));
      
      // Update the state
      setSubscriptionItem(prev => {
        if (!prev) return null;
        return {
          ...prev,
          orders: formattedOrders
        };
      });
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível carregar as ordens'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshInvoices = async () => {
    if (!id || !subscriptionItem) return;
    
    setLoading(true);
    try {
      // First, get all invoices for this subscription
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          amount_due,
          currency,
          due_date,
          paid_date,
          status
        `)
        .eq('subscription_id', subscriptionItem.subscription_id);

      if (invoicesError) {
        console.error('Erro ao buscar faturas:', invoicesError);
        throw invoicesError;
      }
      
      // Then for each invoice, check if it has items with our subscription_plan_id
      const invoicesWithRelevantItems = [];
      
      for (const invoice of invoicesData || []) {
        const { data: invoiceItems, error: itemsError } = await supabase
          .from('invoice_items')
          .select('*')
          .eq('invoice_id', invoice.id)
          .eq('subscription_plan_id', subscriptionItem.subscription_plan_id);
          
        if (itemsError) {
          console.error(`Error checking items for invoice ${invoice.id}:`, itemsError);
          continue;
        }
        
        // If the invoice has items with this subscription plan, include it
        if (invoiceItems && invoiceItems.length > 0) {
          invoicesWithRelevantItems.push({
            id: invoice.id,
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.invoice_date,
            amount_due: invoice.amount_due,
            currency: invoice.currency,
            due_date: invoice.due_date,
            paid_date: invoice.paid_date,
            status: invoice.status
          });
        }
      }
      
      // Update the state using the spread operator
      setSubscriptionItem(prev => {
        if (!prev) return null;
        return {
          ...prev,
          invoice_items: invoicesWithRelevantItems
        };
      });
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível carregar as faturas'
      });
    } finally {
      setLoading(false);
    }
  };

  const getOperationTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'criação': 'Criação',
      'aumento': 'Aumento',
      'redução': 'Redução',
      'cancelamento': 'Cancelamento',
      'renovação': 'Renovação',
      'faturamento': 'Faturamento'
    };
    
    return types[type.toLowerCase()] || type;
  };

  const getBillingPeriodLabel = (periodType: string | null | undefined, duration: string | number | null | undefined) => {
    if (!periodType || !duration) return '-';
    
    const durNum = typeof duration === 'string' ? parseInt(duration) : duration;
    if (isNaN(durNum)) return '-';

    switch (periodType.toLowerCase()) {
      case 'month':
        return durNum === 1 ? 'Mensal' : `${durNum} Meses`;
      case 'year':
        return durNum === 1 ? 'Anual' : `${durNum} Anos`;
      case 'day':
        return durNum === 1 ? 'Diário' : `${durNum} Dias`;
      case 'week':
         return durNum === 1 ? 'Semanal' : `${durNum} Semanas`;
      default:
        return `${durNum} ${periodType}`;
    }
  };

  const handleQuantityChange = async () => {
    if (!subscriptionItem || !id) return;
    
    setLoading(true);
    
    try {
      console.log('Alterando quantidade do item:', id);
      console.log('Tipo de alteração:', quantityChangeType);
      console.log('Valor da alteração:', originalQuantity);
      
      // Calcular a nova quantidade
      const currentQuantity = subscriptionItem.quantity;
      const changeValue = quantityChangeType === 'aumento' 
        ? originalQuantity 
        : -originalQuantity;
      const newQuantity = currentQuantity + changeValue;
      
      console.log('Quantidade atual:', currentQuantity);
      console.log('Valor da alteração:', changeValue);
      console.log('Nova quantidade:', newQuantity);
      
      // Validar a nova quantidade
      if (newQuantity <= 0) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: 'A quantidade não pode ser menor ou igual a zero'
        });
        setLoading(false);
        return;
      }
      
      // Atualizar a quantidade no item da assinatura
      const { error: updateError } = await supabase
        .from('subscription_items')
        .update({ quantity: newQuantity })
        .eq('id', id);
      
      if (updateError) {
        console.error('Erro ao atualizar quantidade no item:', updateError);
        throw updateError;
      }
      
      // Registrar a operação na tabela de orders (agora usando subscription_id em vez de subscription_item_id)
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: subscriptionItem.subscription_id, // Usar subscription_id em vez de subscription_item_id
          operation_type: quantityChangeType,
          quantity_change: changeValue,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        console.error('Erro ao registrar operação na tabela orders:', orderError);
        throw orderError;
      }
      
      console.log('Operação registrada com sucesso na tabela orders');
      
      // Atualizar os dados do item
      const updatedItem = {
        ...subscriptionItem,
        quantity: newQuantity
      };
      
      setSubscriptionItem(updatedItem);
      
      // Atualizar a lista de orders
      refreshOrders();
      
      toast({
        title: "Sucesso",
        description: `Quantidade ${quantityChangeType === 'aumento' ? 'aumentada' : 'reduzida'} com sucesso`
      });
      setIsQuantityChangeDialogOpen(false);
    } catch (error) {
      console.error('Erro ao alterar quantidade:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível alterar a quantidade: ' + (error instanceof Error ? error.message : 'Erro desconhecido')
      });
    } finally {
      setLoading(false);
    }
  };

  const openQuantityChangeDialog = (type: 'aumento' | 'redução') => {
    setQuantityChangeType(type);
    setOriginalQuantity(subscriptionItem?.quantity || 0);
    setIsQuantityChangeDialogOpen(true);
  };

  const handleCancelItem = async () => {
    if (!subscriptionItem || !id) return;
    
    if (!cancellationReason) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Por favor, selecione um motivo para o cancelamento'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Formatar o motivo do cancelamento
      const formattedReason = cancelReason;
      
      // Atualizar o item da assinatura
      const { error: updateError } = await supabase
        .from('subscription_items')
        .update({ 
          state: 'canceled',
          canceled_at: new Date().toISOString(),
          cancellation_reason: formattedReason
        })
        .eq('id', id);
      
      if (updateError) {
        throw updateError;
      }
      
      // Registrar a operação na tabela de orders
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: subscriptionItem.subscription_id,
          subscription_item_id: id,
          subscription_plan_id: subscriptionItem.subscription_plan_id,
          operation_type: 'cancelamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
          notes: `Item cancelado: ${formattedReason}`
        });
      
      if (orderError) {
        throw orderError;
      }
      
      // Verificar se existem outros itens ativos na assinatura
      const { data: activeItems, error: activeItemsError } = await supabase
        .from('subscription_items')
        .select('id')
        .eq('subscription_id', subscriptionItem.subscription_id)
        .eq('state', 'active')
        .neq('id', id); // Excluir o item atual que acabou de ser cancelado
      
      if (activeItemsError) {
        console.error('Erro ao verificar itens ativos:', activeItemsError);
        throw activeItemsError;
      }
      
      // Se não houver outros itens ativos, cancelar a assinatura também
      if (!activeItems || activeItems.length === 0) {
        console.log('Não há outros itens ativos na assinatura. Cancelando a assinatura...');
        
        // Atualizar o estado da assinatura para "canceled"
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .update({
            state: 'canceled',
            canceled_at: new Date().toISOString(),
            cancellation_reason: `Cancelamento automático: ${formattedReason}`,
            
          })
          .eq('id', subscriptionItem.subscription_id);
        
        if (subscriptionError) {
          console.error('Erro ao cancelar assinatura:', subscriptionError);
          toast({
            variant: "destructive",
            title: "Atenção",
            description: 'Item cancelado com sucesso, mas não foi possível cancelar a assinatura automaticamente.'
          });
        } else {
          // Registrar a operação de cancelamento da assinatura
          const { error: subscriptionOrderError } = await supabase
            .from('orders')
            .insert({
              subscription_id: subscriptionItem.subscription_id,
              operation_type: 'cancelamento',
              quantity_change: null,
              operation_date: new Date().toISOString(),
              notes: `Assinatura cancelada automaticamente: Todos os itens foram cancelados. Motivo original: ${formattedReason}`
            });
          
          if (subscriptionOrderError) {
            console.error('Erro ao registrar operação de cancelamento da assinatura:', subscriptionOrderError);
          }
          
          // Atualizar o estado da assinatura no objeto local
          if (subscriptionItem.subscription) {
            const updatedSubscription = {
              ...subscriptionItem.subscription,
              state: 'canceled'
            };
            
            // Atualizar o objeto subscriptionItem com a assinatura atualizada
            setSubscriptionItem(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                subscription: updatedSubscription
              };
            });
          }
          
          toast({
            title: "Sucesso",
            description: 'Item cancelado e assinatura cancelada automaticamente por não haver mais itens ativos.'
          });
        }
      } else {
        toast({
          title: "Sucesso",
          description: 'Item cancelado com sucesso'
        });
      }
      
      // Atualizar os dados do item
      const updatedItem = {
        ...subscriptionItem,
        state: 'canceled',
        canceled_at: new Date().toISOString(),
        cancellation_reason: formattedReason
      };
      
      setSubscriptionItem(updatedItem);
      
      // Atualizar a lista de orders
      refreshOrders();
      
      setIsCancelDialogOpen(false);
    } catch (error) {
      console.error('Erro ao cancelar item:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível cancelar o item: ' + (error instanceof Error ? error.message : 'Erro desconhecido')
      });
    } finally {
      setLoading(false);
    }
  };

  const openCancelDialog = () => {
    setCancelReason('');
    setIsCancelDialogOpen(true);
  };

  const generateInvoiceNumber = () => {
    // Gerar números aleatórios para a primeira parte (6 dígitos)
    let firstPart = '';
    for (let i = 0; i < 6; i++) {
      firstPart += Math.floor(Math.random() * 10).toString();
    }
    
    // Gerar números aleatórios para a segunda parte (3 dígitos)
    let secondPart = '';
    for (let i = 0; i < 3; i++) {
      secondPart += Math.floor(Math.random() * 10).toString();
    }
    
    // Combinar as partes com o hífen
    return `${firstPart}-${secondPart}`;
  };

  const handleGenerateInvoice = async () => {
    if (!subscriptionItem || !id) return;
    
    if (!invoiceDate || !invoiceDueDate || !invoiceAmount) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Por favor, preencha todos os campos obrigatórios'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Buscar todos os itens ativos da assinatura
      const { data: activeItems, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          subscription_plan_id,
          subscription_plan:subscription_plan_id (
            id,
            name,
            price,
            currency
          )
        `)
        .eq('subscription_id', subscriptionItem.subscription_id)
        .neq('state', 'canceled');
      
      if (itemsError) {
        throw itemsError;
      }
      
      if (!activeItems || activeItems.length === 0) {
        toast({
          variant: "destructive",
          title: "Atenção",
          description: 'Não há itens ativos nesta assinatura'
        });
        return;
      }
      
      // Calcular o valor total de todos os itens ativos
      const totalAmount = activeItems.reduce((sum, item) => {
        return sum + (item.quantity * item.unit_price);
      }, 0);
      
      // Criar a fatura principal
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          subscription_id: subscriptionItem.subscription_id, // Referência direta à assinatura
          invoice_number: generateInvoiceNumber(),
          invoice_date: invoiceDate.toISOString(),
          amount_due: invoiceAmount,
          currency: subscriptionItem.currency_code || 'BRL',
          due_date: invoiceDueDate.toISOString(),
          status: 'pending',
          created_by: 'manual',
          updated_by: 'manual'
        })
        .select()
        .single();
      
      if (invoiceError) {
        throw invoiceError;
      }
      
      // Criar os itens da fatura
      const invoiceItemsToInsert = activeItems.map(item => ({
        invoice_id: invoice.id, // Referência à fatura criada
        subscription_plan_id: item.subscription_plan_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        billing_period: null,
        billing_period_unit: null,
        currency_code: subscriptionItem.currency_code || 'BRL',
        amount_price: (item.quantity * item.unit_price / totalAmount) * invoiceAmount,
        created_by: 'manual',
        updated_by: 'manual'
      }));
      
      const { error: invoiceItemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItemsToInsert);
      
      if (invoiceItemsError) {
        throw invoiceItemsError;
      }
      
      // Registrar a operação na tabela de orders (agora relacionada à subscription)
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: subscriptionItem.subscription_id,
          operation_type: 'faturamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        throw orderError;
      }
      
      // Atualizar a lista de faturas
      refreshInvoices();
      
      toast({
        title: "Sucesso",
        description: 'Fatura gerada com sucesso para todos os itens ativos da assinatura'
      });
      setIsInvoiceDialogOpen(false);
    } catch (error) {
      console.error('Erro ao gerar fatura:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível gerar a fatura: ' + (error instanceof Error ? error.message : 'Erro desconhecido')
      });
    } finally {
      setLoading(false);
    }
  };

  const openInvoiceDialog = async () => {
    if (!subscriptionItem) return;
    
    try {
      // Buscar todos os itens ativos da assinatura
      const { data: activeItems, error: itemsError } = await supabase
        .from('subscription_items')
        .select(`
          id,
          quantity,
          unit_price,
          subscription_plan:subscription_plan_id (
            name
          )
        `)
        .eq('subscription_id', subscriptionItem.subscription_id)
        .neq('state', 'canceled');
      
      if (itemsError) {
        throw itemsError;
      }
      
      if (!activeItems || activeItems.length === 0) {
        toast({
          variant: "destructive",
          title: "Atenção",
          description: 'Não há itens ativos nesta assinatura para faturar'
        });
        return;
      }
      
      // Calcular o valor total de todos os itens ativos
      const totalAmount = activeItems.reduce((sum, item) => {
        return sum + (item.quantity * item.unit_price);
      }, 0);
      
      // Gerar um número de fatura padrão
      const invoiceNumber = generateInvoiceNumber();
      
      // Definir a data da fatura como hoje
      setInvoiceDate(new Date());
      
      // Definir a data de vencimento como 30 dias a partir de hoje
      setInvoiceDueDate(new Date(new Date().setDate(new Date().getDate() + 30)));
      
      // Definir o valor da fatura como o valor total de todos os itens ativos
      setInvoiceAmount(totalAmount);
      
      // Armazenar os itens ativos para exibição no modal
      setInvoices(activeItems);
      
      setIsInvoiceDialogOpen(true);
    } catch (error) {
      console.error('Erro ao preparar geração de fatura:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível preparar a geração de fatura'
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h2 className="mt-4 text-xl font-extrabold">Carregando detalhes do item da assinatura...</h2>
        </div>
      </div>
    );
  }

  if (!subscriptionItem) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-destructive text-xl">
            <h2 className="font-extrabold">Erro</h2>
            <p className="mt-2">Item da assinatura não encontrado</p>
          </div>
          <Button className="mt-6" onClick={() => navigate('/subscriptions')}>
            Voltar para Assinaturas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Item de Assinatura: {subscriptionItem?.subscription_plan.name}</h1>
          <p className="text-muted-foreground">
            Assinatura #{subscriptionItem?.subscription_id.substring(0, 8)} | Cliente: {subscriptionItem?.subscription.customer_relationship.customer.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(`/subscriptions/${subscriptionItem?.subscription_id}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <MoreHorizontal className="h-4 w-4 mr-2" />
                Ações
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Gerenciar Item</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => openQuantityChangeDialog('aumento')}
                disabled={subscriptionItem?.state === 'canceled'}
              >
                <Plus className="h-4 w-4 mr-2" />
                Aumentar Quantidade
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => openQuantityChangeDialog('redução')}
                disabled={subscriptionItem?.state === 'canceled' || subscriptionItem?.quantity === 1}
              >
                <Minus className="h-4 w-4 mr-2" />
                Reduzir Quantidade
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={subscriptionItem?.state === 'canceled'}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Renovar Item
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={openInvoiceDialog}
                disabled={subscriptionItem?.state === 'canceled' || subscriptionItem?.subscription?.state !== 'active'}
              >
                <FileText className="h-4 w-4 mr-2" />
                Gerar Fatura
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-destructive"
                onClick={openCancelDialog}
                disabled={subscriptionItem?.state === 'canceled'}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cancelar Item
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Status Card */}
      <Card className={`mb-6 ${
        subscriptionItem?.state === 'active' ? 'border-green-500 bg-green-50' : 
        subscriptionItem?.state === 'canceled' ? 'border-red-500 bg-red-50' : 
        'border-yellow-500 bg-yellow-50'
      }`}>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-6 justify-between">
            <div className="flex items-center gap-2">
              <div className="font-extrabold text-muted-foreground">Status:</div>
              <div>
                <StatusBadge status={subscriptionItem?.state} context="subscription" />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div className="font-extrabold">
                Início: {subscriptionItem?.current_term_start ? formatDate(subscriptionItem.current_term_start) : '-'}
                {subscriptionItem?.current_term_end && ` | Fim: ${formatDate(subscriptionItem.current_term_end)}`}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <div className="font-extrabold">
                Quantidade: {subscriptionItem?.quantity} | Valor: {formatCurrency(subscriptionItem?.unit_price || 0, subscriptionItem?.currency_code || 'BRL')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="mb-6" onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="orders">Operações</TabsTrigger>
          <TabsTrigger value="invoices">Faturas</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Informações do Plano</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-muted-foreground">Nome do Plano:</div>
                  <div className="font-extrabold">{subscriptionItem.subscription_plan.name}</div>
                  
                  <div className="text-muted-foreground">Descrição:</div>
                  <div>{subscriptionItem.subscription_plan.description || '-'}</div>
                  
                  <div className="text-muted-foreground">Família:</div>
                  <div>{subscriptionItem.subscription_plan.family?.name || '-'}</div>
                  
                  <div className="text-muted-foreground">SKU:</div>
                  <div>{subscriptionItem.subscription_plan.sku || '-'}</div>
                  
                  <div className="text-muted-foreground">Preço Base:</div>
                  <div>{formatCurrency(
                    subscriptionItem.subscription_plan.prices?.[0]?.price_amount || 0, 
                    subscriptionItem.subscription_plan.prices?.[0]?.currency || 'BRL'
                  )}</div>
                  
                  <div className="text-muted-foreground">Frequência de Cobrança:</div>
                  <div>{subscriptionItem.subscription_plan.billing_frequency || '-'}</div>
                  
                  <div className="text-muted-foreground">Termo de Fidelidade:</div>
                  <div>{subscriptionItem.subscription_plan.billing_period_type && subscriptionItem.subscription_plan.billing_period_duration ? 
                    `${subscriptionItem.subscription_plan.billing_period_duration} ${subscriptionItem.subscription_plan.billing_period_type === 'month' ? 'meses' : 
                    subscriptionItem.subscription_plan.billing_period_type === 'year' ? 'anos' : 
                    subscriptionItem.subscription_plan.billing_period_type}` : '-'}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Detalhes do Item</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-muted-foreground">Quantidade:</div>
                  <div>{subscriptionItem.quantity}</div>
                  
                  <div className="text-muted-foreground">Preço Unitário:</div>
                  <div>{formatCurrency(subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}</div>
                  
                  <div className="text-muted-foreground">Valor Total:</div>
                  <div>{formatCurrency(subscriptionItem.quantity * subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}</div>
                  
                  <div className="text-muted-foreground">Período de Cobrança:</div>
                  <div>{getBillingPeriodLabel(subscriptionItem.subscription_plan?.billing_period_type, subscriptionItem.subscription_plan?.billing_period_duration)}</div>
                  
                  <div className="text-muted-foreground">Próxima Cobrança:</div>
                  <div>{subscriptionItem.next_billing_at ? formatDate(subscriptionItem.next_billing_at) : '-'}</div>
                  
                  <div className="text-muted-foreground">Data de Cancelamento:</div>
                  <div>{subscriptionItem.canceled_at ? formatDate(subscriptionItem.canceled_at) : '-'}</div>
                  
                  <div className="text-muted-foreground">Motivo do Cancelamento:</div>
                  <div>{subscriptionItem.cancellation_reason || '-'}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Informações da Assinatura</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-muted-foreground mb-1">ID da Assinatura</div>
                    <div className="font-extrabold">{subscriptionItem.subscription_id}</div>
                  </div>
                  
                  <div>
                    <div className="text-muted-foreground mb-1">Status da Assinatura</div>
                    <div>{getStatusBadge(subscriptionItem.subscription.state)}</div>
                  </div>
                  
                  <div>
                    <div className="text-muted-foreground mb-1">Cliente</div>
                    <div className="font-extrabold">{subscriptionItem.subscription.customer_relationship.customer.name}</div>
                  </div>
                  
                  <div>
                    <div className="text-muted-foreground mb-1">Tenant</div>
                    <div>{subscriptionItem.subscription.customer_relationship.relationship}</div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Button variant="outline" onClick={() => navigate(`/subscriptions/${subscriptionItem.subscription_id}`)}>
                    Ver Detalhes da Assinatura
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Operações</CardTitle>
                  <CardDescription>Histórico de operações realizadas neste item</CardDescription>
                </div>
                <Button variant="outline" onClick={refreshOrders}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : subscriptionItem.orders && subscriptionItem.orders.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data da Operação</TableHead>
                      <TableHead>Tipo de Operação</TableHead>
                      <TableHead>Alteração de Quantidade</TableHead>
                      <TableHead>Data de Criação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptionItem.orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{formatDate(order.operation_date)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            order.operation_type === 'criação' || order.operation_type === 'aumento' || order.operation_type === 'renovação' 
                              ? 'default' 
                              : order.operation_type === 'redução' 
                                ? 'secondary' 
                                : 'destructive'
                          }>
                            {getOperationTypeLabel(order.operation_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {order.quantity_change !== null ? (
                            <span className={
                              order.quantity_change > 0 
                                ? 'text-green-600' 
                                : order.quantity_change < 0 
                                  ? 'text-red-600' 
                                  : ''
                            }>
                              {order.quantity_change > 0 ? '+' : ''}{order.quantity_change}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma operação encontrada para este item.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Faturas</CardTitle>
                  <CardDescription>Histórico de faturas deste item</CardDescription>
                </div>
                <Button variant="outline" onClick={refreshInvoices}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : subscriptionItem.invoice_items && subscriptionItem.invoice_items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número da Fatura</TableHead>
                      <TableHead>Data da Fatura</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Data de Vencimento</TableHead>
                      <TableHead>Data de Pagamento</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptionItem.invoice_items.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-extrabold">{invoice.invoice_number}</TableCell>
                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount_due, invoice.currency)}</TableCell>
                        <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</TableCell>
                        <TableCell>{invoice.paid_date ? formatDate(invoice.paid_date) : '-'}</TableCell>
                        <TableCell>
                          <StatusBadge status={invoice.status} context="invoice" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma fatura encontrada para este item.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de alteração de quantidade */}
      <Dialog open={isQuantityChangeDialogOpen} onOpenChange={setIsQuantityChangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {quantityChangeType === 'aumento' ? 'Aumentar' : 'Reduzir'} Quantidade
            </DialogTitle>
            <DialogDescription>
              {quantityChangeType === 'aumento' 
                ? 'Informe a quantidade que deseja adicionar ao plano.' 
                : 'Informe a quantidade que deseja reduzir do plano.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantidade atual</Label>
              <Input 
                id="current-quantity" 
                value={subscriptionItem?.quantity || 0} 
                disabled 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quantity-change">
                {quantityChangeType === 'aumento' ? 'Quantidade a adicionar' : 'Quantidade a reduzir'}
              </Label>
              <Input 
                id="quantity-change" 
                type="number" 
                min={1} 
                max={quantityChangeType === 'redução' ? ((subscriptionItem?.quantity || 1) - 1) : undefined}
                value={originalQuantity} 
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < 1) {
                    setOriginalQuantity(1);
                  } else if (quantityChangeType === 'redução' && value >= (subscriptionItem?.quantity || 1)) {
                    setOriginalQuantity((subscriptionItem?.quantity || 1) - 1);
                  } else {
                    setOriginalQuantity(value);
                  }
                }} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-quantity">Nova quantidade</Label>
              <Input 
                id="new-quantity" 
                value={
                  quantityChangeType === 'aumento' 
                    ? (subscriptionItem?.quantity || 0) + originalQuantity 
                    : (subscriptionItem?.quantity || 0) - originalQuantity
                } 
                disabled 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-total">Novo valor total</Label>
              <Input 
                id="new-total" 
                value={
                  subscriptionItem 
                    ? formatCurrency(
                        ((quantityChangeType === 'aumento' 
                          ? (subscriptionItem.quantity + originalQuantity) 
                          : (subscriptionItem.quantity - originalQuantity)) * subscriptionItem.unit_price), 
                        subscriptionItem.currency_code || 'BRL'
                      ) 
                    : '-'
                } 
                disabled 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsQuantityChangeDialogOpen(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleQuantityChange}
              disabled={
                loading || 
                originalQuantity < 1 || 
                (quantityChangeType === 'redução' && originalQuantity >= (subscriptionItem?.quantity || 1))
              }
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando
                </>
              ) : (
                'Confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de cancelamento */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Cancelar Item de Assinatura
            </DialogTitle>
            <DialogDescription>
              Esta ação irá cancelar o item de assinatura. Esta operação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Motivo do Cancelamento</Label>
              <Select 
                value={cancelReason} 
                onValueChange={setCancelReason}
              >
                <SelectTrigger id="cancel-reason">
                  <SelectValue placeholder="Selecione um motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Solicitação do cliente">Solicitação do cliente</SelectItem>
                  <SelectItem value="Inadimplência">Inadimplência</SelectItem>
                  <SelectItem value="Migração de plano">Migração de plano</SelectItem>
                  <SelectItem value="Insatisfação com o serviço">Insatisfação com o serviço</SelectItem>
                  <SelectItem value="Erro operacional">Erro operacional</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cancel-details">Detalhes (opcional)</Label>
              <Textarea 
                id="cancel-details" 
                placeholder="Forneça mais detalhes sobre o motivo do cancelamento"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="bg-amber-50 p-4 rounded-md border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-extrabold text-amber-800">Atenção</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    O cancelamento deste item irá:
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>Interromper a cobrança deste item</li>
                      <li>Manter o histórico de operações e faturas</li>
                      <li>Registrar o cancelamento no histórico de operações</li>
                      <li className="font-extrabold">Cancelar automaticamente a assinatura se este for o único item ativo</li>
                    </ul>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={loading}
            >
              Voltar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelItem}
              disabled={loading || !cancelReason}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando
                </>
              ) : (
                'Confirmar Cancelamento'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de geração de fatura */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Gerar Nova Fatura para a Assinatura
            </DialogTitle>
            <DialogDescription>
              Esta fatura incluirá todos os itens ativos da assinatura.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-number">Número da Fatura</Label>
              <Input 
                id="invoice-number" 
                value={generateInvoiceNumber()} 
                disabled 
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data da Fatura</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-extrabold"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {invoiceDate ? format(invoiceDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDate}
                      onSelect={(date) => date && setInvoiceDate(date)}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>Data de Vencimento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-extrabold"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {invoiceDueDate ? format(invoiceDueDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDueDate}
                      onSelect={(date) => setInvoiceDueDate(date)}
                      initialFocus
                      locale={ptBR}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invoice-amount">Valor Total da Fatura</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  R$
                </span>
                <Input 
                  id="invoice-amount" 
                  type="number"
                  value={invoiceAmount} 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setInvoiceAmount(0);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setInvoiceAmount(numValue);
                      }
                    }
                  }}
                  className="pl-8"
                  placeholder="0,00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Itens Incluídos na Fatura</Label>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plano</TableHead>
                      <TableHead className="text-center">Quantidade</TableHead>
                      <TableHead>Preço Unitário</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-extrabold">{item.subscription_plan?.name || 'Plano sem nome'}</TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price, subscriptionItem?.currency_code || 'BRL')}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.quantity * item.unit_price, subscriptionItem?.currency_code || 'BRL')}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-extrabold">Total:</TableCell>
                      <TableCell className="text-right font-extrabold">
                        {formatCurrency(
                          invoices.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0),
                          subscriptionItem?.currency_code || 'BRL'
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
              <div className="flex items-start gap-2">
                <Receipt className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-extrabold text-blue-800">Informações</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    A fatura será gerada para todos os itens ativos da assinatura com status "Pendente".
                    O valor será distribuído proporcionalmente entre os itens com base em seus valores individuais.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsInvoiceDialogOpen(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleGenerateInvoice}
              disabled={
                loading || 
                !invoiceDate || 
                !invoiceDueDate || 
                invoiceAmount === 0
              }
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando
                </>
              ) : (
                'Gerar Fatura'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionItemDetail; 